
import { useState, useEffect, useCallback } from 'react';
import { dataSyncService } from '../services/dataSyncService';
import { dataStorageService } from '../services/dataStorageService';
import { useAuthStore } from '../../app/hooks/useAuthStore';
import { notificationService } from '../lib/notification/notification';


/**
 * useDataSync hook for managing private updates from server.
 */
export const useDataSync = () => {
    const { hashA, hashB, logout } = useAuthStore();
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
    const [updateInfo, setUpdateInfo] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState(null);

    const checkUpdates = useCallback(async () => {
        // console.log('Checking for updates...');
        const result = await dataSyncService.checkUpdate(hashA);
        
        if (result.authError === 'DELETED_USER') {
            notificationService.error('Your account was removed. Switching to Guest mode.', 5000);
            logout();
            await dataStorageService.clearAll();
            window.location.reload();
            return;
        }

        if (result.isRequired) {
            const localManifest = await dataStorageService.getVersionInfo();
            const isFirstLoad = !localManifest;
            const authChanged = localManifest && ((hashA && !localManifest.isPersonal) || (!hashA && localManifest.isPersonal));
            
            if (isFirstLoad || authChanged) {
                // console.log('First load or Auth changed: starting automatic download');
                setIsUpdating(true);
                try {
                    await dataSyncService.performUpdate(result.manifest, result.changedFiles, hashB);
                    setIsUpdating(false);
                    // Refresh if auth changed to update the UI with new content
                    if (authChanged) {
                        window.location.reload();
                    }
                } catch (err) {
                    // console.error('Update failed:', err);
                    setError(err);
                    setIsUpdating(false);
                }
            } else {
                // console.log('Update available: waiting for user confirmation');
                setIsUpdateAvailable(true);
                setUpdateInfo(result);
            }
        } else if (result.error) {
            setError(result.error);
        }
    }, [hashA, hashB]);

    useEffect(() => {
        checkUpdates();
    }, [checkUpdates]);

    const confirmUpdate = async () => {
        if (!updateInfo) return;
        
        setIsUpdating(true);
        try {
            await dataSyncService.performUpdate(updateInfo.manifest, updateInfo.changedFiles, hashB);
            setIsUpdateAvailable(false);
            setUpdateInfo(null);
        } catch (err) {
            setError(err);
        } finally {
            setIsUpdating(false);
        }
    };

    const dismissUpdate = () => {
        setIsUpdateAvailable(false);
    };

    return {
        isUpdateAvailable,
        updateInfo,
        isUpdating,
        error,
        confirmUpdate,
        dismissUpdate,
        checkUpdates
    };
};
